import type {Token} from '../../../types'
import {isListType} from '../../utils'

/** Creates a listItem token with the given start and end positions. */
function createListItemToken(direction: 'enter' | 'exit', start: number, end: number): Token {
  return {
    direction,
    type: 'listItem',
    start,
    end,
  }
}

/** Open the list item in the stack by creating an enter token. Adds the enter token from the stack. */
function openListItem(result: Token[], listItemStack: Token[], startPosition: number, endPosition: number): void {
  const listItemEnterToken = createListItemToken('enter', startPosition, endPosition)
  result.push(listItemEnterToken)
  listItemStack.push(listItemEnterToken)
}

/**
 * Closes the last list item in the stack by creating an exit token and updating the enter token's end position.
 * Removes the enter token from the stack.
 */
function closeListItem(result: Token[], listItemStack: Token[], endPosition: number): void {
  const listItemEnterToken = listItemStack.pop()!
  listItemEnterToken.end = endPosition
  const listItemExitToken = createListItemToken('exit', listItemEnterToken.start, endPosition)
  result.push(listItemExitToken)
}

/** Handles list enter/exit tokens and manages the list stack. Closes the last open list item at the end of a list. */
function handleListToken(token: Token, result: Token[], listStack: Token[], listItemStack: Token[]): void {
  if (token.direction === 'enter') {
    listStack.push(token)
  } else {
    // List exit
    const listDepth = listStack.length
    if (listItemStack.length >= listDepth) {
      closeListItem(result, listItemStack, token.end)
    }
    listStack.pop()
  }
}

/** Wrap each list item in our custom `listItem` token for rendering. */
export function createListItemTokens(tokens: Token[]): Token[] {
  const result: Token[] = []
  const listStack: Token[] = []
  const listItemStack: Token[] = []

  for (let index = 0; index < tokens.length; index++) {
    const token = tokens[index]!

    if (isListType(token.type)) {
      handleListToken(token, result, listStack, listItemStack)
    } else if (token.type === 'listItemPrefix' && token.direction === 'enter') {
      // Adjust end of enter and exit listItem token to account for listItemIndent
      // Does NOT affect consumed tokens
      const consumedTokens: Token[] = []
      while (result.length && result.at(-1)?.type === 'listItemIndent') {
        consumedTokens.push(result.pop()!)
      }
      result.push(...consumedTokens.reverse())

      while (listItemStack.length >= listStack.length) {
        // Close previous listItem
        closeListItem(result, listItemStack, consumedTokens[0]?.start ?? token.start)
      }

      if (listStack.length > listItemStack.length) {
        // Should open a new list item
        openListItem(result, listItemStack, token.start, token.end)
      }
    }

    result.push(token)
  }

  return result
}
